import { useTranslation } from 'react-i18next';
import { computeTripPayout, formatSar, type RateUnit } from '@/lib/trip-math';

interface ScenariosTableProps {
  targetQty: number;
  clientRateAtTarget: number;
  investorRateAtTarget: number;
  driverRateAtTarget: number;
  rateUnit?: RateUnit;
}

const SCENARIOS = [
  { fraction: 1.0,  label: '100%' },
  { fraction: 0.95, label: '95%' },
  { fraction: 0.9,  label: '90%' },
  { fraction: 0.75, label: '75%' },
  { fraction: 0.5,  label: '50%' },
];

/**
 * Side-by-side breakdown: what each party gets at various delivery levels.
 * Helps the user reason about the cut structure when negotiating rates.
 */
export function ScenariosTable(props: ScenariosTableProps) {
  const { t } = useTranslation();
  const { targetQty, clientRateAtTarget, investorRateAtTarget, driverRateAtTarget, rateUnit } = props;

  if (!targetQty || !clientRateAtTarget) {
    return null;
  }

  const scenarios = SCENARIOS.map((s) => ({
    label: s.label,
    fraction: s.fraction,
    result: computeTripPayout({
      deliveredQty: targetQty * s.fraction,
      targetQty,
      clientRateAtTarget,
      investorRateAtTarget,
      driverRateAtTarget,
      rateUnit,
    }),
  }));

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="bg-muted/30 px-4 py-3 border-b border-border">
        <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
          {t('contracts.scenarios.title')}
        </h4>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          {t('contracts.scenarios.subtitle')}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/20 border-b border-border">
            <tr>
              <th className="text-start px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                {t('contracts.scenarios.delivery')}
              </th>
              <th className="text-end px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                {t('contracts.preview.clientPays')}
              </th>
              <th className="text-end px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                {t('contracts.preview.companyKeep')}
              </th>
              <th className="text-end px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                {t('contracts.preview.investorNet')}
              </th>
              <th className="text-end px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                {t('contracts.preview.driverPay')}
              </th>
            </tr>
          </thead>
          <tbody>
            {scenarios.map((s) => (
              <tr key={s.label} className={`border-b border-border last:border-b-0 ${s.fraction === 1 ? 'bg-emerald-500/5' : ''}`}>
                <td className="px-3 py-2 font-mono text-sm">{s.label}</td>
                <td className="px-3 py-2 text-end font-mono tabular-nums">{formatSar(s.result.clientPayment, { showCurrency: false })}</td>
                <td className="px-3 py-2 text-end font-mono tabular-nums font-medium text-foreground">{formatSar(s.result.companyKeep, { showCurrency: false })}</td>
                <td className="px-3 py-2 text-end font-mono tabular-nums text-blue-600 dark:text-blue-400">{formatSar(s.result.investorNet, { showCurrency: false })}</td>
                <td className="px-3 py-2 text-end font-mono tabular-nums text-purple-600 dark:text-purple-400">{formatSar(s.result.driverPay, { showCurrency: false })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 border-t border-border bg-muted/10 text-[11px] text-muted-foreground">
        💡 {t('contracts.scenarios.note')}
      </div>
    </div>
  );
}
